
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { SEO } from "@/components/common/SEO";
import { generatePageStructuredData } from "@/utils/seoUtils";
import { OAuthCallbackHandler } from "@/components/auth/OAuthCallbackHandler";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { AuthErrorDisplay } from "@/components/auth/AuthErrorDisplay";
import { AuthProcessingState } from "@/components/auth/AuthProcessingState";
import { AuthModeToggle } from "@/components/auth/AuthModeToggle";
import { AuthContent } from "@/components/auth/AuthContent";
import { MobileAuthForm } from "@/components/mobile/MobileAuthForm";
import { MobileViewportMeta } from "@/components/mobile/MobileViewportMeta";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<"signin" | "signup" | "forgotPassword" | "resetPassword">("signin");
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get mode from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get("mode");
    
    if (modeParam === "signup") {
      setMode("signup");
    } else if (modeParam === "forgotPassword") {
      setMode("forgotPassword");
    } else if (modeParam === "resetPassword") {
      setMode("resetPassword");
    } else {
      setMode("signin");
    }
    
    setError(null);
  }, [location.search]);

  // If user is already logged in, redirect to dashboard or redirect URL
  useEffect(() => {
    if (session) {
      const params = new URLSearchParams(location.search);
      const redirectUrl = params.get("redirect");
      
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate("/dashboard");
      }
    }
  }, [session, navigate, location.search]);

  const handleTabChange = (value: string) => {
    setError(null);
    navigate(`/auth?mode=${value}`);
  };

  const handleForgotPassword = () => {
    setError(null);
    navigate("/auth?mode=forgotPassword");
  };

  const handleBackToSignIn = () => {
    setError(null);
    navigate("/auth");
  };

  const handleToggleMode = () => {
    const newMode = mode === "signin" ? "signup" : "signin";
    handleTabChange(newMode);
  };

  // Check for access_token in URL (OAuth callback)
  const isOAuthCallback = location.hash.includes("access_token");

  const structuredData = generatePageStructuredData(
    "Sign In or Register | Hour Tracker",
    "Sign in to your Hour Tracker account or create a new one to start tracking your billable hours and generating professional invoices.",
    window.location.href
  );

  if (isOAuthCallback) {
    return <OAuthCallbackHandler setIsProcessing={setIsProcessing} setAuthError={setError} />;
  }

  return (
    <>
      <MobileViewportMeta />
      <div className="min-h-screen flex items-center justify-center bg-muted/20 mobile-section-padding">
        <SEO 
          title="Sign In or Register | Hour Tracker"
          description="Sign in to your Hour Tracker account or create a new one to start tracking your billable hours and generating professional invoices."
          structuredData={structuredData}
        />
        
        <div className="w-full max-w-md">
          <AuthHeader mode={mode} />
          
          {error && <AuthErrorDisplay error={error} />}
          
          {isProcessing ? (
            <AuthProcessingState />
          ) : (
            <div className="bg-background rounded-lg shadow-md border mobile-card">
              {isMobile && (mode === "signin" || mode === "signup") ? (
                <div className="p-6">
                  <MobileAuthForm 
                    mode={mode as "signin" | "signup"}
                    onToggleMode={handleToggleMode}
                    onForgotPassword={handleForgotPassword}
                  />
                </div>
              ) : (
                <div className="p-6">
                  <AuthContent
                    mode={mode}
                    onTabChange={handleTabChange}
                    onForgotPassword={handleForgotPassword}
                    onBackToSignIn={handleBackToSignIn}
                  />
                </div>
              )}
            </div>
          )}
          
          {!isMobile && (
            <AuthModeToggle 
              mode={mode} 
              onToggleMode={(newMode) => handleTabChange(newMode)} 
              isProcessing={isProcessing}
            />
          )}
        </div>
      </div>
    </>
  );
}
